import React from "react"
import { Modal, Form, Col, Button, Spinner } from "react-bootstrap"
import { FaAngleDown } from "react-icons/fa"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import Checkbox from "../../Checkbox"
import LabAccountListGet from "../../../api/Administration/LabAccountListGet"
import LabAccount from "../../../api/Administration/Model/LabAccount"
import ActionButton from "../../ActionButton"
import { APIError } from "../../../api/ObservableFromFetch"
import { Typeahead } from "react-bootstrap-typeahead"
import GovGetPanels from "../../../api/Randoms/GovGetPanels"
import { AdditionalTest, GetAdditionalTests, UpdateAdditionalTests } from "../../../api/Administration/AdditionalTests"
import { GovPanel, SampleType } from "../../../api/Randoms/GovPoolModel/CollectionTypeResponse"

interface AdditionalTestingProps {
  portalMode?: boolean
  locationMode?: boolean
  implementationID: number
  accountID: number
  show: boolean
  onClose: () => void
}

interface AdditionalTestingState {
  labAccountsLoading: boolean
  labAccounts: LabAccount[]
  labAccountSelection?: LabAccount
  panelsLoading: boolean
  panels: GovPanel[]
  panelSelection: string[]
  loading: boolean
  saving: boolean
  showConfirmSave: boolean
}

export default class AdditionalTesting extends React.PureComponent<AdditionalTestingProps, AdditionalTestingState> {
  subscription?: Subscription
  loadLabAccountsSubscription?: Subscription
  loadPanelSubscription?: Subscription
  labAccountRef = React.createRef<Typeahead<LabAccount>>()
  state: AdditionalTestingState = {
    labAccountSelection: undefined,
    labAccountsLoading: false,
    panelsLoading: false,
    panels: [],
    panelSelection: [],
    loading: false,
    saving: false,
    labAccounts: [],
    showConfirmSave: false,
  }

  componentDidMount() {
    this.loadLabAccounts()
  }

  componentDidUpdate(prevProps: AdditionalTestingProps) {
    if (this.props.show && this.props.show !== prevProps.show) {
      this.loadLabAccounts()
    }
  }

  componentWillUnmount() {
    this.loadLabAccountsSubscription && this.loadLabAccountsSubscription.unsubscribe()
    this.loadPanelSubscription && this.loadPanelSubscription.unsubscribe()
    this.subscription && this.subscription.unsubscribe()
  }

  onCancel = () => {
    this.props.onClose()
    this.setState({ saving:false, loading:false })
  }

  onSubmit = () => {
    this.saveData()
  }

  saveData = () => {
    if (!this.state.labAccountSelection?.LabAccountNumber) {
      return
    }
    this.setState({ saving: true })
    this.subscription = UpdateAdditionalTests(this.props.accountID, this.state.labAccountSelection?.LabAccountNumber, this.state.panels.map(p => p.ID), this.state.panelSelection).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
        this.onCancel()
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  loadLabAccounts() {
    this.setState({ labAccountsLoading: true })
    this.loadLabAccountsSubscription && this.loadLabAccountsSubscription.unsubscribe()
    this.loadLabAccountsSubscription = LabAccountListGet(this.props.implementationID, this.props.accountID, false).subscribe(
      data => this.setState({ labAccounts: data.ActiveLabAccount, labAccountsLoading: false }),
      error => this.handleAPIError(error, { labAccountsLoading: false })
    )
  }

  loadPanels = (selectedLabAccount?: LabAccount) => {
    if (!selectedLabAccount) {
      return
    }

    this.setState({ panelsLoading: true, panels: [] })
    this.loadPanelSubscription && this.loadPanelSubscription.unsubscribe()
    this.loadPanelSubscription = GovGetPanels(selectedLabAccount.LabAccountNumber).subscribe(
      (data: SampleType[]) => this.setState({ panelsLoading: false, panels: data.map(st => st.Panels).reduce((acc, val) => acc.concat(val), []) }, () => {
        this.loadAdditionalTests(selectedLabAccount.LabAccountNumber)
      }),
      (error) => this.handleAPIError(error, { panelsLoading: false })
    )
  }

  loadAdditionalTests = (labAccountNumber: string) => {
    this.setState({ loading: true })
    this.subscription && this.subscription.unsubscribe()
    this.subscription = GetAdditionalTests(this.props.accountID, labAccountNumber).subscribe(
      (data: AdditionalTest[]) => this.setState({ loading: false, panelSelection: data.map(t => t.PanelCode) }),
      (error) => this.handleAPIError(error, { loading: false })
    )
  }

  handleLabChange = (items: LabAccount[]) => {
    const selectedLab = items[0]
    if (selectedLab === this.state.labAccountSelection) { return }
    this.setState({ labAccounts: [...this.state.labAccounts], labAccountSelection: selectedLab }, () => {
      this.loadPanels(selectedLab)
    })
  }

  handlePanelChange = (panelId: string) => {
    const newPanelSelection: string[] = this.state.panelSelection.includes(panelId) ? [...this.state.panelSelection.filter(p => p != panelId)] : [...this.state.panelSelection, panelId]
    this.setState({ panelSelection: newPanelSelection })
  }

  render() {
    const { labAccountsLoading, panelsLoading, loading, saving } = this.state
    const disableSave = !this.state.labAccountSelection?.LabAccountNumber || labAccountsLoading || panelsLoading || loading

    return (
      <>
        <Modal backdrop="static" show={this.props.show} size="xl">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Additional Testing</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>Select the test codes that you want displayed as Additional Tests for this company</div>
            <Form className="px-4">
              <Form.Row className="form-group align-items-center">
                <Form.Label column sm={4} htmlFor="labAccount">Account Number</Form.Label>
                <Col className="text-right">{labAccountsLoading && <Spinner animation="border" variant="secondary" size="sm" style={{ margin: "auto" }} />}</Col>
                <Col sm={7}>
                  {<Typeahead id="labAccount"
                    ref={this.labAccountRef}
                    labelKey="LabAccountNumber"
                    emptyLabel="No Laboratory Accounts Found"
                    disabled={labAccountsLoading}
                    //filterBy={(option, props) => showLabForSelectedCollectionType(option, this.state.sampleTypes.selected?.Description || "")}
                    onChange={this.handleLabChange}
                    options={this.state.labAccounts || []}
                    selected={this.state.labAccountSelection ? [this.state.labAccountSelection] : []}
                    placeholder="Enter the Lab Account">
                    <Button variant="toggle"
                      disabled={labAccountsLoading}
                      onClick={() => { this.labAccountRef.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>
                  </Typeahead>}
                </Col>
              </Form.Row>
              {panelsLoading && <Spinner animation="border" variant="secondary" size="sm" style={{ margin: "auto" }} />}
              <Form.Row className="form-group align-items-center">
                {this.state.panels.map((item: GovPanel, key: number) => <Col md={6} key={key}><Checkbox key={`panel-${item.ID}`} id={`panel-${item.ID}`} label={item.Name} value={item.ID} onChange={(e) => this.handlePanelChange(e.target.value)} checked={this.state.panelSelection.includes(item.ID)} /></Col>)}
              </Form.Row>
              {(panelsLoading || loading) && <Spinner animation="border" variant="secondary" size="sm" style={{ margin: "auto" }} />}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.onCancel} className="px-4">Cancel</Button>
            <ActionButton onClick={this.onSubmit} className="px-4" spin={saving} disableOnSpin disabled={disableSave}>Update</ActionButton>
          </Modal.Footer>
        </Modal>
      </>
    )
  }

}
